import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listInvoices } from '@/lib/invoices';
import { useAuth } from '@/state/AuthContext';
import { Icon } from '@/components/ui';
import { formatMoney } from '@/lib/format';
import type { Invoice } from '@/types/invoice';

interface DailyTask {
  id: string;
  date: string; // YYYY-MM-DD
  title: string;
  done: boolean;
}

const DEFAULT_TASKS: DailyTask[] = [
  { id: 't-1', date: new Date().toISOString().slice(0, 10), title: 'Vérifier extraction OCR de la dernière facture', done: true },
  { id: 't-2', date: new Date().toISOString().slice(0, 10), title: 'Relance paiement client Atlas Technologies', done: false },
  { id: 't-3', date: new Date().toISOString().slice(0, 10), title: 'Envoyer devis validé #DEV-2026-04', done: false },
  { id: 't-4', date: new Date().toISOString().slice(0, 10), title: 'Télécharger export comptable XLSX / PDF', done: false },
];

export function Overview() {
  const { user } = useAuth();
  const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'year'>('today');
  const [filterCategory, setFilterCategory] = useState<'all' | 'paid' | 'pending' | 'draft' | 'tasks'>('all');
  
  // Calendar State: Default to current year & month
  const today = new Date();
  const [currentYear, setCurrentYear] = useState<number>(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState<number>(today.getMonth()); // 0-indexed
  const [selectedDay, setSelectedDay] = useState<string | null>(today.toISOString().slice(0, 10));

  // Search input state
  const [searchQuery, setSearchQuery] = useState('');

  // Daily Tasks state persisted in localStorage
  const [tasks, setTasks] = useState<DailyTask[]>(() => {
    try {
      const saved = localStorage.getItem('myinvoice_daily_tasks_v2');
      if (saved) return JSON.parse(saved);
    } catch {
      /* ignore */
    }
    return DEFAULT_TASKS;
  });
  const [newTaskInput, setNewTaskInput] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem('myinvoice_daily_tasks_v2', JSON.stringify(tasks));
    } catch {
      /* ignore */
    }
  }, [tasks]);

  // Fetch all invoices
  const { data: invoiceData } = useQuery({
    queryKey: ['invoices-overview'],
    queryFn: () => listInvoices({ pageSize: 100 }),
    staleTime: 10_000,
  });

  const invoices: Invoice[] = invoiceData?.items ?? [];

  // Group invoices by date string (YYYY-MM-DD)
  const invoicesByDay = useMemo(() => {
    const map = new Map<string, Invoice[]>();
    for (const inv of invoices) {
      const dateStr = (inv.issueDate || inv.createdAt || '').slice(0, 10);
      if (dateStr) {
        if (!map.has(dateStr)) map.set(dateStr, []);
        map.get(dateStr)!.push(inv);
      }
    }
    return map;
  }, [invoices]);

  // Financial aggregates
  const stats = useMemo(() => {
    let totalInvoicedMinor = 0;
    let totalCollectedMinor = 0;
    let totalPendingMinor = 0;
    let currency = 'MAD';

    for (const inv of invoices) {
      if (inv.currency) currency = inv.currency;
      totalInvoicedMinor += inv.total || 0;
      if (inv.status === 'ready' || inv.status === 'edited') {
        totalCollectedMinor += inv.total || 0;
      } else {
        totalPendingMinor += inv.total || 0;
      }
    }

    // Realistic fallback display amounts if database is fresh
    const finalInvoiced = totalInvoicedMinor > 0 ? totalInvoicedMinor : 874500;
    const finalCollected = totalCollectedMinor > 0 ? totalCollectedMinor : 700000;
    const finalPending = totalPendingMinor > 0 ? totalPendingMinor : 174500;

    const rate = Math.round((finalCollected / finalInvoiced) * 100);

    return {
      totalInvoiced: finalInvoiced,
      totalCollected: finalCollected,
      totalPending: finalPending,
      currency,
      rate: Math.min(100, Math.max(0, rate)),
    };
  }, [invoices]);

  // Filtered invoices for the activity cards
  const displayedInvoices = useMemo(() => {
    let list = invoices;

    // Filter by calendar day if clicked
    if (selectedDay) {
      const dayInvoices = invoicesByDay.get(selectedDay);
      if (dayInvoices && dayInvoices.length > 0) {
        list = dayInvoices;
      }
    }

    // Filter by category tab
    if (filterCategory === 'paid') {
      list = list.filter((i) => i.status === 'ready');
    } else if (filterCategory === 'pending') {
      list = list.filter((i) => i.status === 'processing');
    } else if (filterCategory === 'draft') {
      list = list.filter((i) => i.status === 'draft' || i.status === 'failed');
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (i) =>
          i.invoiceNumber?.toLowerCase().includes(q) ||
          i.vendor?.name?.toLowerCase().includes(q) ||
          i.buyer?.name?.toLowerCase().includes(q)
      );
    }

    return list;
  }, [invoices, selectedDay, invoicesByDay, filterCategory, searchQuery]);

  // Tasks for selected day
  const displayedTasks = useMemo(() => {
    if (!selectedDay) return tasks;
    return tasks.filter((t) => t.date === selectedDay || !t.date);
  }, [tasks, selectedDay]);

  // Calendar calculations
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const calendarDays = useMemo(() => {
    const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay(); // 0 is Sun
    // Adjust so Mon is 0, Sun is 6
    const startOffset = (firstDayIndex + 6) % 7;
    const totalDaysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const prevMonthDays = new Date(currentYear, currentMonth, 0).getDate();

    const cells: { day: number; isCurrentMonth: boolean; dateStr: string; hasInvoice: boolean }[] = [];

    // Prev month overflow days
    for (let i = startOffset - 1; i >= 0; i--) {
      const dayNum = prevMonthDays - i;
      const m = currentMonth === 0 ? 12 : currentMonth;
      const y = currentMonth === 0 ? currentYear - 1 : currentYear;
      const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
      cells.push({ day: dayNum, isCurrentMonth: false, dateStr, hasInvoice: invoicesByDay.has(dateStr) });
    }

    // Current month days
    for (let i = 1; i <= totalDaysInMonth; i++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      cells.push({ day: i, isCurrentMonth: true, dateStr, hasInvoice: invoicesByDay.has(dateStr) });
    }

    // Next month overflow days (to fill complete 35 or 42 grid cells)
    const remaining = (7 - (cells.length % 7)) % 7;
    for (let i = 1; i <= remaining; i++) {
      const m = currentMonth === 11 ? 1 : currentMonth + 2;
      const y = currentMonth === 11 ? currentYear + 1 : currentYear;
      const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      cells.push({ day: i, isCurrentMonth: false, dateStr, hasInvoice: invoicesByDay.has(dateStr) });
    }

    return cells;
  }, [currentYear, currentMonth, invoicesByDay]);

  function handlePrevMonth() {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  }

  function handleNextMonth() {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  }

  function toggleTaskDone(id: string) {
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t))
    );
  }

  function deleteTask(id: string) {
    setTasks((prev) => prev.filter((t) => t.id !== id));
  }

  function addTask() {
    if (!newTaskInput.trim()) return;
    const newTask: DailyTask = {
      id: `task-${Date.now()}`,
      date: selectedDay || today.toISOString().slice(0, 10),
      title: newTaskInput.trim(),
      done: false,
    };
    setTasks((prev) => [newTask, ...prev]);
    setNewTaskInput('');
  }

  // Greeting name
  const userName = user?.name || user?.email?.split('@')[0] || 'Alex';

  // SVG Gauge calculations (circle radius 36, circumference ~ 226)
  const gaugeCircumference = 2 * Math.PI * 34; // ~213.6
  const gaugeOffset = gaugeCircumference - (stats.rate / 100) * gaugeCircumference;

  return (
    <div className="neo-main-content">
      {/* Top Header */}
      <header className="neo-header">
        <div className="neo-greeting">
          <h1>Bonjour, {userName} !</h1>
          <p>Prêt pour le suivi de votre facturation & de vos finances ?</p>
        </div>

        <div className="neo-header-actions">
          <div className="neo-search-pill">
            <Icon name="search" size={16} />
            <input
              type="text"
              placeholder="Rechercher factures, clients…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <Link to="/dashboard/create" className="neo-btn-pill primary">
            <Icon name="plus" size={16} />
            <span>+ Nouvelle facture</span>
          </Link>

          <Link to="/dashboard/upload" className="neo-btn-pill lime">
            <Icon name="scan" size={16} />
            <span>Scan OCR</span>
          </Link>
        </div>
      </header>

      {/* Top Grid: Financial Curves (Left) + Royal Blue Calendar (Right) */}
      <div className="neo-grid-top">
        {/* Left Card: Financial Activity & Wave Curves */}
        <div className="neo-card">
          <div className="neo-card-header">
            <h2 className="neo-card-title">Activité financière</h2>
            <select
              className="neo-pill-select"
              value={period}
              onChange={(e) => setPeriod(e.target.value as any)}
            >
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="year">Cette année</option>
            </select>
          </div>

          {/* 3 Distinct Smooth Wave Charts */}
          <div className="neo-curves-wrapper">
            {/* Curve 1: Neon Lime Wave with Peak Badge */}
            <div className="neo-curve-col">
              <div className="neo-curve-svg-box">
                {/* Glowing peak badge */}
                <div className="neo-chart-badge lime" style={{ left: '55%', top: '22%' }}>
                  8 745
                </div>

                <svg viewBox="0 0 200 100" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="tealGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#ed5c25" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#ed5c25" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {/* Dashed vertical peak drop line */}
                  <line x1="110" y1="26" x2="110" y2="95" stroke="#ed5c25" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.6" />
                  
                  {/* Area fill */}
                  <path
                    d="M 0 80 Q 25 75 50 65 T 100 45 T 110 26 T 130 55 T 165 40 T 200 65 L 200 100 L 0 100 Z"
                    fill="url(#tealGrad)"
                  />
                  {/* Main stroke line */}
                  <path
                    d="M 0 80 Q 25 75 50 65 T 100 45 T 110 26 T 130 55 T 165 40 T 200 65"
                    fill="none"
                    stroke="#ed5c25"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  {/* Peak Marker Dot */}
                  <circle cx="110" cy="26" r="4.5" fill="#ed5c25" stroke="#ffffff" strokeWidth="2" />
                </svg>
              </div>
            </div>

            {/* Curve 2: Dual Wave (Invoiced vs Collected) with legend */}
            <div className="neo-curve-col">
              <div className="neo-curve-svg-box">
                <div className="neo-chart-badge" style={{ left: '50%', top: '15%', background: '#ed5c25', color: '#ffffff' }}>
                  700
                </div>

                <svg viewBox="0 0 200 100" preserveAspectRatio="none">
                  {/* Invoiced Wave (Ma Facture orange) */}
                  <path
                    d="M 0 70 C 35 75, 60 20, 100 18 C 140 18, 160 80, 200 60"
                    fill="none"
                    stroke="#ed5c25"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  {/* Collected Wave (soft apricot) */}
                  <path
                    d="M 0 85 C 40 85, 70 50, 100 48 C 130 48, 160 30, 200 45"
                    fill="none"
                    stroke="#e99a5b"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <circle cx="100" cy="18" r="4" fill="#ed5c25" stroke="#ffffff" strokeWidth="2" />
                </svg>
              </div>
              <div className="neo-curve-legend">
                <div className="neo-legend-item">
                  <span className="neo-legend-dot" style={{ background: '#ed5c25' }} />
                  <span>Facturé</span>
                </div>
                <div className="neo-legend-item">
                  <span className="neo-legend-dot" style={{ background: '#e99a5b' }} />
                  <span>Encaissé</span>
                </div>
              </div>
            </div>

            {/* Curve 3: Slate Wave with peak point */}
            <div className="neo-curve-col">
              <div className="neo-curve-svg-box">
                <div className="neo-chart-badge" style={{ left: '60%', top: '24%', background: 'rgba(237, 92, 37, 0.12)', color: '#bd451d', border: '1px solid rgba(237, 92, 37, 0.25)' }}>
                  Actif
                </div>

                <svg viewBox="0 0 200 100" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="slateGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#e99a5b" stopOpacity="0.22" />
                      <stop offset="100%" stopColor="#e99a5b" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 0 75 C 30 75, 60 90, 90 60 C 110 40, 120 28, 140 45 C 160 60, 180 35, 200 50 L 200 100 L 0 100 Z"
                    fill="url(#slateGrad)"
                  />
                  <path
                    d="M 0 75 C 30 75, 60 90, 90 60 C 110 40, 120 28, 140 45 C 160 60, 180 35, 200 50"
                    fill="none"
                    stroke="#e99a5b"
                    strokeWidth="2.8"
                    strokeLinecap="round"
                  />
                  <circle cx="120" cy="28" r="4" fill="#e99a5b" stroke="#ffffff" strokeWidth="2" />
                </svg>
              </div>
            </div>
          </div>

          {/* Metrics summary row directly underneath */}
          <div className="neo-metrics-row">
            <div className="neo-metric-item">
              <span className="neo-metric-label">Total facturé</span>
              <span className="neo-metric-val">{formatMoney(stats.totalInvoiced, stats.currency)}</span>
              <div className="neo-metric-sub">
                <span>Cible : 10k</span>
                <span>Moy : 9.4k</span>
              </div>
            </div>

            <div className="neo-metric-item">
              <span className="neo-metric-label">Encaissé & Réglé</span>
              <span className="neo-metric-val">{formatMoney(stats.totalCollected, stats.currency)}</span>
              <div className="neo-metric-sub">
                <span>Objectif : 8k</span>
                <span>Moy : 6.8k</span>
              </div>
            </div>

            <div className="neo-metric-item">
              <span className="neo-metric-label">En attente / Brouillons</span>
              <span className="neo-metric-val">{formatMoney(stats.totalPending, stats.currency)}</span>
              <div className="neo-metric-sub">
                <span>Nombre : {invoices.length}</span>
                <span>Est. : ~2j</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Card: Your Active Days (Royal Blue Calendar) */}
        <div className="neo-card-blue">
          <div className="neo-card-header">
            <h2 className="neo-card-title">Vos jours d'activité</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                type="button"
                className="neo-pill-select"
                onClick={handlePrevMonth}
                aria-label="Mois précédent"
                style={{ padding: '4px 10px' }}
              >
                ‹
              </button>
              <span style={{ fontWeight: 600, fontSize: 13 }}>
                {monthNames[currentMonth]} {currentYear}
              </span>
              <button
                type="button"
                className="neo-pill-select"
                onClick={handleNextMonth}
                aria-label="Mois suivant"
                style={{ padding: '4px 10px' }}
              >
                ›
              </button>
            </div>
          </div>

          {/* Weekday headers */}
          <div className="neo-cal-weekdays">
            <span>LUN</span>
            <span>MAR</span>
            <span>MER</span>
            <span>JEU</span>
            <span>VEN</span>
            <span>SAM</span>
            <span>DIM</span>
          </div>

          {/* Days Grid */}
          <div className="neo-cal-grid">
            {calendarDays.map((cell, idx) => {
              const isSelected = selectedDay === cell.dateStr;
              return (
                <button
                  key={`${cell.dateStr}-${idx}`}
                  type="button"
                  className={`neo-cal-cell ${!cell.isCurrentMonth ? 'overflow' : ''} ${
                    isSelected ? 'active-day' : ''
                  }`}
                  onClick={() => setSelectedDay(isSelected ? null : cell.dateStr)}
                  title={`${cell.dateStr}${cell.hasInvoice ? ' (Factures enregistrées)' : ''}`}
                >
                  <span>{cell.day}</span>
                  {cell.hasInvoice && <span className="neo-cal-dot" />}
                </button>
              );
            })}
          </div>

          {/* Active day filter bar */}
          {selectedDay && (
            <div className="neo-cal-filter-status">
              <span>
                Filtré par jour : <strong>{selectedDay}</strong>
              </span>
              <button type="button" onClick={() => setSelectedDay(null)}>
                Tout afficher
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Grid: Collection Ring + Revenue Target (Left) + Activities & Tasks (Right) */}
      <div className="neo-grid-bottom">
        {/* Left Side: 2 Small Cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Circular Progress Gauge */}
          <div className="neo-card neo-gauge-card">
            <div className="neo-gauge-content">
              <div className="neo-gauge-text">
                <h3>Taux de recouvrement</h3>
                <p>Efficacité des règlements</p>
                <div style={{ marginTop: 12, fontSize: 12, color: 'var(--neo-text-3)' }}>
                  Objectif cible : 90%
                </div>
              </div>

              {/* Neon Lime Circular Gauge */}
              <div className="neo-gauge-ring">
                <svg viewBox="0 0 80 80">
                  {/* Background Track */}
                  <circle
                    cx="40"
                    cy="40"
                    r="34"
                    fill="none"
                    stroke="#eee4dd"
                    strokeWidth="7"
                  />
                  {/* Progress Arc: Ma Facture orange */}
                  <circle
                    cx="40"
                    cy="40"
                    r="34"
                    fill="none"
                    stroke="#ed5c25"
                    strokeWidth="7"
                    strokeDasharray={gaugeCircumference}
                    strokeDashoffset={gaugeOffset}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 0.8s ease' }}
                  />
                </svg>
                <div className="neo-gauge-center-text">{stats.rate}%</div>
              </div>
            </div>

            <div className="neo-gauge-footer">
              <span>Statut : Conforme au cycle de facturation en cours</span>
            </div>
          </div>

          {/* Target Progress Bar */}
          <div className="neo-card neo-target-card">
            <div className="neo-target-header">
              <h4>Objectif de chiffre d'affaires</h4>
              <span className="pct">80% atteint</span>
            </div>

            <div className="neo-target-track">
              <div className="neo-target-bar" style={{ width: '80%' }} />
            </div>

            <div className="neo-target-labels">
              <span>0 {stats.currency}</span>
              <span>Cible : 10 000 {stats.currency}</span>
            </div>
          </div>
        </div>

        {/* Right Side: My Activities & Invoices */}
        <div className="neo-card" style={{ padding: 24 }}>
          <div className="neo-activities-section">
            <div className="neo-activities-header">
              <h3>Mes activités & Factures</h3>
              <span className="count-pill">
                {displayedInvoices.length} factures actives / {displayedTasks.length} tâches
              </span>
            </div>

            {/* Filter Tabs */}
            <div className="neo-filter-tabs">
              <button
                type="button"
                className={`neo-filter-tab ${filterCategory === 'all' ? 'active' : ''}`}
                onClick={() => setFilterCategory('all')}
              >
                Toutes
              </button>
              <button
                type="button"
                className={`neo-filter-tab ${filterCategory === 'paid' ? 'active' : ''}`}
                onClick={() => setFilterCategory('paid')}
              >
                Payées
              </button>
              <button
                type="button"
                className={`neo-filter-tab ${filterCategory === 'pending' ? 'active' : ''}`}
                onClick={() => setFilterCategory('pending')}
              >
                En attente
              </button>
              <button
                type="button"
                className={`neo-filter-tab ${filterCategory === 'draft' ? 'active' : ''}`}
                onClick={() => setFilterCategory('draft')}
              >
                Brouillons
              </button>
              <button
                type="button"
                className={`neo-filter-tab ${filterCategory === 'tasks' ? 'active' : ''}`}
                onClick={() => setFilterCategory('tasks')}
              >
                Tâches & Rappels
              </button>
            </div>

            {/* If Tasks Tab is chosen or tasks exist */}
            {filterCategory === 'tasks' ? (
              <div className="neo-tasks-panel">
                <div className="neo-tasks-header">
                  <h4>
                    <Icon name="check-circle" size={18} />
                    <span>Tâches de facturation ({selectedDay ? `du ${selectedDay}` : 'Tous les jours'})</span>
                  </h4>
                </div>

                <div className="neo-task-add-bar">
                  <input
                    type="text"
                    className="neo-task-input"
                    placeholder="Ajouter une tâche ou un rappel pour aujourd'hui…"
                    value={newTaskInput}
                    onChange={(e) => setNewTaskInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addTask()}
                  />
                  <button type="button" className="neo-btn-pill lime" onClick={addTask}>
                    + Ajouter
                  </button>
                </div>

                <div className="neo-tasks-list">
                  {displayedTasks.length === 0 ? (
                    <div style={{ color: 'var(--neo-text-4)', fontSize: 13, padding: '12px 0' }}>
                      Aucune tâche prévue pour ce jour. Ajoutez-en une ci-dessus !
                    </div>
                  ) : (
                    displayedTasks.map((task) => (
                      <div key={task.id} className="neo-task-item">
                        <div className="neo-task-left" onClick={() => toggleTaskDone(task.id)}>
                          <div className={`neo-task-check ${task.done ? 'done' : ''}`}>
                            {task.done && <Icon name="check" size={12} />}
                          </div>
                          <span className={`neo-task-title ${task.done ? 'done' : ''}`}>
                            {task.title}
                          </span>
                        </div>

                        <button
                          type="button"
                          className="neo-task-del"
                          onClick={() => deleteTask(task.id)}
                          title="Supprimer la tâche"
                        >
                          <Icon name="trash" size={14} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              /* Horizontal Activity Cards Grid */
              <div className="neo-activity-cards-grid">
                {/* Featured Card 1: Electric Royal Blue Card */}
                {displayedInvoices[0] ? (
                  <Link
                    to={`/dashboard/invoices/${displayedInvoices[0].id}`}
                    className="neo-act-card featured-blue"
                  >
                    <div className="neo-act-top">
                      <div className="neo-avatar-stack">
                        <span className="neo-mini-avatar">
                          {displayedInvoices[0].buyer?.name?.slice(0, 2).toUpperCase() || 'CL'}
                        </span>
                        <span className="neo-mini-avatar">
                          {displayedInvoices[0].vendor?.name?.slice(0, 2).toUpperCase() || 'VN'}
                        </span>
                      </div>
                      <Icon name="arrow-up-right" size={18} className="neo-act-arrow" />
                    </div>

                    <div className="neo-act-body">
                      <h4>{displayedInvoices[0].buyer?.name || 'Client Principal'}</h4>
                      <p>
                        {formatMoney(displayedInvoices[0].total, displayedInvoices[0].currency)} •{' '}
                        {displayedInvoices[0].status.toUpperCase()}
                      </p>
                    </div>
                  </Link>
                ) : (
                  <Link to="/dashboard/create" className="neo-act-card featured-blue">
                    <div className="neo-act-top">
                      <div className="neo-avatar-stack">
                        <span className="neo-mini-avatar">EX</span>
                      </div>
                      <Icon name="arrow-up-right" size={18} className="neo-act-arrow" />
                    </div>
                    <div className="neo-act-body">
                      <h4>Nouveau cycle</h4>
                      <p>Créer votre première facture</p>
                    </div>
                  </Link>
                )}

                {/* Card 2: Matte Dark Card */}
                {displayedInvoices[1] ? (
                  <Link
                    to={`/dashboard/invoices/${displayedInvoices[1].id}`}
                    className="neo-act-card dark-surface"
                  >
                    <div className="neo-act-top">
                      <div className="neo-avatar-stack">
                        <span className="neo-mini-avatar">
                          {displayedInvoices[1].buyer?.name?.slice(0, 2).toUpperCase() || 'CL'}
                        </span>
                      </div>
                      <Icon name="arrow-up-right" size={18} className="neo-act-arrow" />
                    </div>

                    <div className="neo-act-body">
                      <h4>{displayedInvoices[1].buyer?.name || 'Facture Récente'}</h4>
                      <p>
                        {formatMoney(displayedInvoices[1].total, displayedInvoices[1].currency)} •{' '}
                        {displayedInvoices[1].invoiceNumber || 'Brouillon'}
                      </p>
                    </div>
                  </Link>
                ) : (
                  <div className="neo-act-card dark-surface">
                    <div className="neo-act-top">
                      <div className="neo-avatar-stack">
                        <span className="neo-mini-avatar">MI</span>
                      </div>
                      <Icon name="arrow-up-right" size={18} className="neo-act-arrow" />
                    </div>
                    <div className="neo-act-body">
                      <h4>Relance client</h4>
                      <p>Suivi automatisé des règlements</p>
                    </div>
                  </div>
                )}

                {/* Card 3: Matte Dark Card */}
                {displayedInvoices[2] ? (
                  <Link
                    to={`/dashboard/invoices/${displayedInvoices[2].id}`}
                    className="neo-act-card dark-surface"
                  >
                    <div className="neo-act-top">
                      <div className="neo-avatar-stack">
                        <span className="neo-mini-avatar">
                          {displayedInvoices[2].buyer?.name?.slice(0, 2).toUpperCase() || 'CL'}
                        </span>
                      </div>
                      <Icon name="arrow-up-right" size={18} className="neo-act-arrow" />
                    </div>

                    <div className="neo-act-body">
                      <h4>{displayedInvoices[2].buyer?.name || 'Devis Proforma'}</h4>
                      <p>
                        {formatMoney(displayedInvoices[2].total, displayedInvoices[2].currency)} •{' '}
                        {displayedInvoices[2].status}
                      </p>
                    </div>
                  </Link>
                ) : (
                  <div className="neo-act-card dark-surface">
                    <div className="neo-act-top">
                      <div className="neo-avatar-stack">
                        <span className="neo-mini-avatar">BK</span>
                      </div>
                      <Icon name="arrow-up-right" size={18} className="neo-act-arrow" />
                    </div>
                    <div className="neo-act-body">
                      <h4>Audit IA Gemini</h4>
                      <p>Précision comptable 100%</p>
                    </div>
                  </div>
                )}

                {/* Card 4: Dashed + Add Activity Card */}
                <Link to="/dashboard/upload" className="neo-act-card dashed-add">
                  <Icon name="plus" size={24} />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>+ Ajouter facture</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
